import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { PlanResponse } from '../lib/api-client';

export function usePlans({ enabled = false }: { enabled?: boolean } = {}) {
  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const fetchInFlightRef = useRef(false);
  const hasLoadedOnce = useRef(false);

  const doFetch = useCallback(async (startingAfter?: string) => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    try {
      const query: Record<string, string> = { limit: '25' };
      if (startingAfter) query.starting_after = startingAfter;
      const res = await apiClientRef.current.listPlans(query);
      if (startingAfter) {
        setPlans((prev) => [...prev, ...res.data]);
      } else {
        setPlans(res.data);
      }
      setHasMore(res.has_more);
      hasLoadedOnce.current = true;
      setStatus('success');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load plans');
      if (!hasLoadedOnce.current) setStatus('error');
    } finally {
      fetchInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    void doFetch();
  }, [enabled, doFetch]);

  const loadMore = useCallback(() => {
    if (!hasMore || fetchInFlightRef.current) return;
    const lastId = plans[plans.length - 1]?.id;
    if (lastId) void doFetch(lastId);
  }, [hasMore, plans, doFetch]);

  const reload = useCallback(() => {
    setStatus('loading');
    void doFetch();
  }, [doFetch]);

  return { plans, status, error, hasMore, loadMore, reload };
}
